import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import BottomSheet from "components/common/BottomSheet";
import { useSnackbar } from "contexts/SnackbarContext";
import { blockUser, createReport } from "api/community";

// 채팅방 상단에서 community_dm 상대를 신고/차단하기 위한 액션 묶음.
// 신고/차단 자체는 커뮤니티와 동일한 BE 계약(api/community)을 재사용한다.
// 차단 "해제"는 의도적으로 제외한다(해제는 커뮤니티 차단 목록에서만 수행).
//
// 렌더 구조 주의: 트리거(점 3개 버튼, PeerMenuButton)는 header 안에, BottomSheet 본체
// (MessageRoomPeerSheets)는 header 바깥(MessageRoom 레벨)에 렌더링해야 한다. header 에는
// backdrop-filter 가 걸려 있어 containing block 이 생기는데, 그 안에서 position:fixed
// BottomSheet 를 렌더하면 overlay 가 viewport 가 아니라 header 기준으로 갇혀버린다.
// 또한 신고/차단 시트는 community_dm 일 때만 mount 되므로, useSnackbar/useNavigate 같은
// 컨텍스트 의존은 그때만 발생한다(다른 타입 방은 SnackbarProvider 없이도 동작).
export const PEER_REPORT_REASONS = [
    { value: "spam", label: "스팸" },
    { value: "harassment", label: "괴롭힘" },
    { value: "hate_speech", label: "혐오 발언" },
    { value: "violence", label: "폭력" },
    { value: "inappropriate", label: "부적절한 콘텐츠" },
    { value: "other", label: "기타" },
];

// 사용자가 사유를 고르지 않고 바로 제출했을 때 사용할 기본 사유.
export const DEFAULT_REPORT_REASON = "inappropriate";

// "이미 차단된 상대" 판별 — Community 차단 API 의 에러 형태를 견고하게 본다.
// 확인된 BE(Community) 계약: 이미 차단이면 ConflictError → HTTP 409 +
// { success:false, message:"User already blocked", error:{ code:"CONFLICT" } }.
// 이 엔드포인트의 409 는 현재 이미 차단 상태만 의미하므로 status 를 우선 신호로 본다.
// BE 가 code 를 ALREADY_BLOCKED 등으로 세분화하거나 게이트웨이가 status 를 가려도
// 견디도록 code/message/detail 문구도 함께 검사한다.
export const isAlreadyBlockedError = (error) => {
    if (!error) return false;
    const response = error.response;
    const data = response?.data;
    if (response?.status === 409) return true;

    // 확인된 계약 코드는 CONFLICT. ALREADY_BLOCKED 는 BE 가 향후 세분화할 때를 위한 선제 방어.
    // (DUPLICATE 처럼 차단과 무관한 409 에도 붙는 범용 코드는 오판 여지가 있어 의도적으로 제외)
    const codes = [data?.error?.code, data?.code]
        .filter(Boolean)
        .map((code) => String(code).toUpperCase());
    if (codes.some((code) => code === "CONFLICT" || code === "ALREADY_BLOCKED")) {
        return true;
    }

    const texts = [data?.message, data?.detail, data?.error?.message, error.message]
        .filter(Boolean)
        .map((text) => String(text).toLowerCase());
    return texts.some((text) => text.includes("already blocked") || text.includes("이미 차단"));
};

// community_dm 상대에 한해, peer 식별자가 있을 때만 신고/차단 액션을 노출한다.
export const isPeerActionable = (room) =>
    room?.type === "community_dm" && Boolean(room?.peer?.userId);

// 신고 description 에 채팅방 맥락을 담는다. 운영자가 이 신고가 어떤 채팅방에서,
// 누구를 대상으로 발생했는지 식별할 수 있게 roomId/상대 이름을 함께 넣는다.
export const buildChatReportDescription = ({ roomId, peerName, detail } = {}) => {
    const head = peerName ? `채팅방 신고 · 상대: ${peerName}` : "채팅방 신고";
    const parts = [roomId ? `${head} (roomId: ${roomId})` : head];
    const trimmed = String(detail || "").trim();
    if (trimmed) parts.push(trimmed);
    return parts.join("\n");
};

// 점 3개 메뉴 버튼 — header 안에 렌더한다. 컨텍스트/상태 의존이 없어
// community_dm 이 아닌 방에서도 안전하게 import 할 수 있다(여기선 렌더만 안 함).
export const PeerMenuButton = ({ open = false, onOpen }) => (
    <button
        type="button"
        className="message-room-header__menu"
        aria-label="대화 상대 메뉴 열기"
        aria-haspopup="true"
        aria-expanded={open}
        onClick={onOpen}
    >
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <circle cx="10" cy="4" r="1.6" fill="currentColor" />
            <circle cx="10" cy="10" r="1.6" fill="currentColor" />
            <circle cx="10" cy="16" r="1.6" fill="currentColor" />
        </svg>
    </button>
);

// 신고/차단 BottomSheet 묶음 — header 바깥(MessageRoom 레벨)에서 렌더한다.
// 메뉴 열림 상태는 트리거 버튼과 공유하기 위해 부모(MessageRoom)에서 끌어올려 받는다.
export const MessageRoomPeerSheets = ({ roomId, peer, menuOpen = false, onMenuClose }) => {
    const navigate = useNavigate();
    const { showSuccess, showError, showInfo } = useSnackbar();

    const [reportOpen, setReportOpen] = useState(false);
    const [blockOpen, setBlockOpen] = useState(false);
    const [selectedReason, setSelectedReason] = useState(null);
    const [detail, setDetail] = useState("");
    const [isReporting, setIsReporting] = useState(false);
    const [isBlocking, setIsBlocking] = useState(false);

    const peerUserId = peer?.userId != null ? String(peer.userId) : "";
    const peerName = peer?.nickname || "상대";

    const closeMenu = () => onMenuClose?.();

    const closeReport = () => {
        setReportOpen(false);
        setSelectedReason(null);
        setDetail("");
    };

    const handleSubmitReport = async () => {
        if (isReporting) return;
        const reason = selectedReason || DEFAULT_REPORT_REASON;
        setIsReporting(true);
        try {
            // BE 가 2xx 로 응답하더라도 body 의 success 플래그를 확인한다.
            // 커뮤니티(api/community) 계약과 동일하게, 2xx + {success:false} 는
            // 실패로 취급하여 성공 토스트/닫기를 하지 않고 에러를 노출한다.
            const result = await createReport({
                target_type: "user",
                target_id: peerUserId,
                reason,
                description: buildChatReportDescription({ roomId, peerName, detail }),
            });
            if (result?.success) {
                showSuccess("신고가 접수되었습니다.");
                closeReport();
            } else {
                showError("신고에 실패했습니다.");
            }
        } catch {
            showError("신고에 실패했습니다.");
        } finally {
            setIsReporting(false);
        }
    };

    const handleBlock = async () => {
        if (isBlocking) return;
        setIsBlocking(true);
        try {
            // 신고와 동일하게 2xx + {success:false} 는 실패로 취급한다.
            // 성공 플래그가 없으면 시트를 닫거나 navigate 하지 않고 에러만 노출한다.
            const result = await blockUser(peerUserId);
            if (result?.success) {
                showSuccess("상대를 차단했습니다.");
                setBlockOpen(false);
                // 차단한 상대와의 방에 머물 이유가 없으므로 이전 화면(목록)으로 빠져나온다.
                navigate(-1);
            } else {
                showError("차단에 실패했습니다.");
            }
        } catch (error) {
            // 이미 차단된 상대를 다시 차단하려는 경우(BE 409 Conflict)는 실패가 아니라
            // "이미 차단됨" 상태이므로 전용 안내를 띄우고, 최종 상태가 성공과 동일하게
            // 차단된 것이므로 시트를 닫고 방을 빠져나온다.
            if (isAlreadyBlockedError(error)) {
                showInfo("이미 차단되었습니다.");
                setBlockOpen(false);
                navigate(-1);
            } else {
                showError("차단에 실패했습니다.");
            }
        } finally {
            setIsBlocking(false);
        }
    };

    // BottomSheet 본체는 header 밖(MessageRoom 레벨)으로 빼도 여전히 App 의
    // PullToRefresh(.ptr-scroll-container, overflow-y:auto) 안에 머문다. 공통 BottomSheet 는
    // portal 없이 position:fixed overlay 를 현재 트리에 그대로 렌더하므로, iOS/WKWebView 에서
    // fixed 가 viewport 가 아니라 스크롤 컨테이너 기준으로 밀릴 수 있다. 이를 회피하기 위해
    // 채팅방 전용 시트 묶음만 document.body 로 portal 한다(공통 컴포넌트는 그대로 둔다).
    // SSR/테스트 등 document 가 없는 환경에서는 기존처럼 fragment 를 그대로 반환한다.
    //
    // 공통 BottomSheet 는 isOpen 과 무관하게 overlay 를 항상 렌더하고, title 이 있으면
    // 고정 id="bottom-sheet-title" 인 <h2> 를 그린다. 신고/차단 시트를 동시에 mount 하면
    // 닫혀 있어도 같은 id 가 DOM 에 둘 이상 존재해 접근성(aria-labelledby) 참조가 모호해진다.
    // 공통 컴포넌트는 건드리지 않고, 현재 열려야 하는 시트만 조건부로 mount 해 중복 id 를 막는다.
    const sheets = (
        <>
            {/* 상대 메뉴 — 신고/차단 진입점 */}
            {menuOpen && (
            <BottomSheet isOpen={menuOpen} onClose={closeMenu} height="auto" className="message-peer-sheet">
                {/* 커뮤니티 유저 메뉴와 동일하게 제목 없이 노출. 시트 제목이 없어 dialog 이름이
                    비므로 메뉴 그룹에 접근성 이름을 준다. */}
                <div className="message-peer-menu" role="group" aria-label="대화 상대 메뉴">
                    <button
                        type="button"
                        className="message-peer-menu__item"
                        onClick={() => {
                            closeMenu();
                            setReportOpen(true);
                        }}
                    >
                        상대 신고하기
                    </button>
                    <button
                        type="button"
                        className="message-peer-menu__item message-peer-menu__item--danger"
                        onClick={() => {
                            closeMenu();
                            setBlockOpen(true);
                        }}
                    >
                        상대 차단하기
                    </button>
                </div>
            </BottomSheet>
            )}

            {/* 신고 사유 선택 */}
            {reportOpen && (
            <BottomSheet isOpen={reportOpen} onClose={closeReport} title="신고 사유 선택" height="auto" className="message-peer-sheet">
                <div className="message-peer-report" role="radiogroup" aria-label="신고 사유 선택">
                    {PEER_REPORT_REASONS.map((reason) => {
                        const checked = selectedReason === reason.value;
                        return (
                            <button
                                key={reason.value}
                                type="button"
                                role="radio"
                                aria-checked={checked}
                                className={`message-peer-report__reason${checked ? " is-selected" : ""}`}
                                onClick={() => setSelectedReason(reason.value)}
                            >
                                {reason.label}
                                {checked && (
                                    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                                        <path d="M3 8.5l3.5 3.5 6.5-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                )}
                            </button>
                        );
                    })}
                    <label className="message-peer-report__detail-label" htmlFor="message-peer-report-detail">
                        상세 내용 (선택)
                    </label>
                    <textarea
                        id="message-peer-report-detail"
                        className="message-peer-report__detail"
                        placeholder="신고 사유를 자세히 적어주세요"
                        value={detail}
                        onChange={(event) => setDetail(event.target.value)}
                        rows={3}
                    />
                    {/* 작은 viewport 에서도 CTA 가 항상 보이도록 sticky footer 로 고정한다.
                        사유 목록/상세는 footer 위에서 스크롤된다. (scope: .message-peer-sheet) */}
                    <div className="message-peer-report__footer">
                        <button
                            type="button"
                            className="message-peer-report__submit"
                            disabled={isReporting}
                            onClick={handleSubmitReport}
                        >
                            {isReporting ? "처리 중..." : "신고하기"}
                        </button>
                    </div>
                </div>
            </BottomSheet>
            )}

            {/* 차단 확인 */}
            {blockOpen && (
            <BottomSheet isOpen={blockOpen} onClose={() => setBlockOpen(false)} title="사용자 차단" height="auto" className="message-peer-sheet">
                <div className="message-peer-block">
                    <p className="message-peer-block__message">
                        {peerName}님을 차단하면 더 이상 메시지를 받지 않습니다. 차단하시겠습니까?
                    </p>
                    <div className="message-peer-block__actions">
                        <button
                            type="button"
                            className="message-peer-block__cancel"
                            onClick={() => setBlockOpen(false)}
                        >
                            취소
                        </button>
                        <button
                            type="button"
                            className="message-peer-block__submit"
                            disabled={isBlocking}
                            onClick={handleBlock}
                        >
                            {isBlocking ? "처리 중..." : "차단하기"}
                        </button>
                    </div>
                </div>
            </BottomSheet>
            )}
        </>
    );

    if (typeof document !== "undefined" && document.body) {
        return createPortal(sheets, document.body);
    }
    return sheets;
};
